import Qs from "qs";
import { useMemo } from "react";
import { DEFAULT_METRIC } from "constants/metrics";

import { IInteractionMetricsAvailable, InteractionMetric } from "@edgetier/watchtower-types";
import { useInteractionMetricsAvailable } from "hooks-for/metrics/use-interaction-metrics-available";

/**
 * get the first metric available
 * @param metricsAvailable
 */
const getFirstMetricAvailable = (metricsAvailable: IInteractionMetricsAvailable) => {
    // Find the first metric available
    const [firstAvailableMetric] = Object.entries(metricsAvailable).filter(([key, value]) => {
        if (value) return key;
        return value;
    });
    const [metricName] = firstAvailableMetric;

    return metricName;
};

/**
 * Find the metric name from the URL or default to the first availble metric
 * @param search URL query string.
 * @returns      Metric name.
 */
export const useFirstMetricAvailable = (search: string) => {
    const { metric } = Qs.parse(search, { ignoreQueryPrefix: true });
    const { data: interactionMetricsAvailable } = useInteractionMetricsAvailable();

    const metricSelected = useMemo(() => {
        if (typeof interactionMetricsAvailable === "undefined") {
            return typeof metric === "string" ? metric : DEFAULT_METRIC;
        }

        // Return the default in the unlikely case of there being no metrics available. Without doing this things will be broken.
        const hasMetricsAvailable = Object.values(interactionMetricsAvailable).some((metric) => metric);
        if (!hasMetricsAvailable) {
            return DEFAULT_METRIC;
        }

        return typeof metric === "string" // Check if metric exists in URL
            ? metric // Send metric
            : interactionMetricsAvailable[DEFAULT_METRIC] // Check if default metric is available
            ? DEFAULT_METRIC // Send default metric
            : getFirstMetricAvailable(interactionMetricsAvailable); // Get the first metric name
    }, [interactionMetricsAvailable, metric]);

    return metricSelected as InteractionMetric;
};
